<template>
  <TemplateBase>
    <main class="relative z-0 col-span-full row-start-2" :class="{ 'xl:row-start-1': page.image }">
      <MoleculeHeaderOverview
        class="mb-8 sm:mb-12 md:mb-8"
        :title="page.title"
        :subtitle="page.subtitle"
        :introduction="page.introduction"
        :image="page.image"
        :class="{ 'mt-16 lg:mt-20': !page.image }"
      />

      <OrganismNavigationBreadcrumb
        class="container absolute z-30 top-0 2xl:-mt-16 mx-auto p-4 md:relative md:mb-16 md:px-8 md:py-0 lg:mb-20"
        :path="breadcrumbs"
        :dark-mode-mobile="!!page.image"
      />

      <section id="collection-page-content" class="container relative grid grid-cols-12 mb-16 mx-auto px-4">
        <div class="padding-page-content relative col-span-full 2xl:col-end-10 2xl:col-start-4 bg-white rounded-xl lg:col-end-12 lg:col-start-2 xl:col-end-11 xl:col-start-3">
          <!-- Page body text -->
          <div v-if="page.description" class="prose prose--app mb-8 max-w-none overflow-auto" v-html="page.description" />

          <OrganismLocationSearchInput
            class="mb-6 max-w-sm"
            :location="location"
            :distance="distance"
            :city="city"
            :errors="errors"
            @location="setLocation"
            @distance="setDistance"
          />

          <ul :class="{ 'c-columns mb-4': collectionDistricts.length > 10, 'mb-6': collectionDistricts.length <= 10 }">
            <li
              v-for="{ title, isSelected } in collectionDistricts"
              :key="title"
              class="mb-3 leading-snug cursor-pointer"
              :class="{ 'inline-block w-full': collectionDistricts.length > 10 }"
              @click="toggleCollectionDistrict(title)"
            >
              <div class="flex">
                <AtomIcon
                  :icon="isSelected ? 'app-checkbox-checked' : 'app-checkbox-unchecked'"
                  class="c-margin-checkbox shrink-0 mr-3 w-4 h-4 text-app-green"
                  :class="{ 'text-app-green': isSelected }"
                />
                <span class="text-sm">{{ title }}</span>
              </div>
            </li>
          </ul>

          <div class="-mb-12">
            <FormieForm handle="collectionTool" :value="{ addition: selectedCollectionDistricts.join('\n') }" @pre-submission="preSubmission" />
          </div>
        </div>
      </section>
    </main>
  </TemplateBase>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { routerQueryIsEqual } from '~/composables/setup-dynamic-url'
import { useCollectionPageStore } from '~/store/collection-page'

interface Props {
  breadcrumbs: Array<any>
  page: {
    title: string
    subtitle: string
    introduction: string
    image: string | null
    description: string | null
  }
}
const route = useRoute()
const router = useRouter()
const collectionPageStore = useCollectionPageStore()

defineProps<Props>()

collectionPageStore.initialize(route.query)
onMounted(() => {
  collectionPageStore.fetch()
})

const city = computed(() => collectionPageStore.city)
const collectionDistricts = computed(() => collectionPageStore.getCollectionDistricts)
const distance = computed(() => collectionPageStore.distance)
const errors = computed(() => collectionPageStore.errors)
const location = computed(() => collectionPageStore.location)
const selectedCollectionDistricts = computed(() => collectionPageStore.selectedCollectionDistricts)
const urlParameters = computed(() => collectionPageStore.urlParameters)

watch(urlParameters, (query) => {
  if (!routerQueryIsEqual(route.query, query)) {
    router.push({ query })
  }
})

const setDistance = (value: any) => collectionPageStore.setDistance(value)
const setLocation = (value: any) => collectionPageStore.setLocation(value)
const toggleCollectionDistrict = (title: string) => collectionPageStore.toggleCollectionDistrict(title)
const preSubmission = (data: any) => {
  data.selection = selectedCollectionDistricts.value.length > 0 ? selectedCollectionDistricts.value.join('\n') : 'Geen selectie gemaakt'
}
</script>

<style lang="css">
.c-columns {
  columns: 300px 2;
  column-gap: 32px;
}

.c-margin-checkbox {
  margin-top: 2px;
}
</style>
